/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Alert, Button, Center, Checkbox, Divider, Loader, Select, Space, Stack, Text } from '@mantine/core';
import { i18n } from '@lingui/core';
import { FC, useContext, useState } from 'react';
import { IconChevronDown, IconSend, IconShieldHalfFilled, IconTemplate } from '@tabler/icons';
import { useForm } from '@mantine/form';
import { EventS } from '../../../../Service/EventS';
import { MessageS } from '../../../../Service/MessageS';
import { RecommendationS } from '../../../../Service/RecommendationS';
import { RecommendationDto } from '../../../../Types/RecommendationT';
import { deepCopy, isEmpty } from '../../../../Utils/utils';
import { useRecommendationMail } from '../../../../Service/hooks/useRecommendationMail';
import { Contact } from '../../../../Service/ContactS';
import { AgencyTemplateDTO } from '../../../../Types/MessageT';
import { openAttachmentSelectionModal } from '../../../../Atoms/AttachmentSelectionModal';
import Attachments from '../../../../Organismns/Attachments';
import { EventBaseInput, EventRichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import { DataS } from '../../../../Service/DataS';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import { useAgencyTemplates } from '../../../../Service/hooks/useAgencyTemplates';
import { openCreateTemplateModal } from '../../../../Templates/Settings/Emails/agency/AgencyTemplateModals';
import { ViolationContext } from '../../../../Service/Context/ViolationContext';
import { EventContext } from '../../../../Service/Context/EventContext';
import { PlaceholderS } from '../../../../Service/PlaceholderS';
import { TiptapS } from '../../../../Service/TiptapS';
import BookitupAlert from '../../../../Atoms/BookitupAlert';
import { EventParticipationsContext } from '../../../../Service/Context/EventParticipationContextV2';

interface Props {
  recommendation: RecommendationDto;
  patchRecommendation: (recommendation: RecommendationDto) => void;
  recommendedContact: Contact;
  closeModal: () => void;
}

const SendCustomerInfoMail: FC<Props> = ({ recommendation, patchRecommendation, recommendedContact, closeModal }) => {
  const { id, eventId, recommendedContactId } = recommendation;
  const { profileId } = useJwtClaims();
  const { interceptViolation } = useContext(ViolationContext);
  const { event } = useContext<any>(EventContext);
  const { customer } = useContext(EventParticipationsContext);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, selectTemplate] = useState<AgencyTemplateDTO>();
  const { templates } = useAgencyTemplates();
  const { mail, attachments, handlers, updateProp, lastUpdate } = useRecommendationMail(
    'SHARE_INFO',
    recommendation,
    selectedTemplate,
  );
  const { subject, message } = mail;
  const { setState: setAttachments, remove: removeAttachment } = handlers;
  const { kind } = event;
  const { email, phoneNumber } = customer;

  const deleteFileAttachment = (index: number) => removeAttachment(index);

  const form = useForm({
    initialValues: {
      message: '',
      includeCustomerName: true,
      includeCustomerPhone: !isEmpty(phoneNumber),
      includeCustomerEmail: !isEmpty(email),
      includeCustomerAddress: true,
      includeLocation: true,
      includeEventKind: kind !== null,
    },
  });

  const onSubmit = async () => {
    setLoading(true);
    const { values } = form;

    const placeholdersResolved = await PlaceholderS.checkPlaceholders({
      profileId,
      eventId: Number(eventId),
      message,
      subject,
    });
    if (placeholdersResolved) {
      const exported = await MessageS.exportEvent(
        eventId,
        recommendedContactId,
        id,
        {
          ...values,
          newVersion: true,
          attachments,
          subject,
          message,
        },
        interceptViolation,
      );
      if (exported) {
        const shared = await EventS.shareEvent(eventId, recommendedContactId, id, values);
        if (shared) {
          RecommendationS.edit(id, { message, shareContact: true }).then(
            (_recommendation) =>
              _recommendation && patchRecommendation({ ...deepCopy(recommendation), lastContactShare: new Date() }),
          );
          closeModal();
        }
      }
    }
    setLoading(false);
  };

  return (
    <>
      <BookitupAlert title="Datenschutzrechtlichen" icon={<IconShieldHalfFilled />}>
        <Text>
          Beachte bitte, dass Du aus datenschutzrechtlichen Gründen die Genehmigung Deines Kunden brauchst, seine
          Kontaktdaten an Dritte weiterzuleiten.
        </Text>
      </BookitupAlert>
      <Text mt="md" mb="md">
        Mit &quot;Senden&quot; übermittelst Du dem Kontakt folgende Infos:
      </Text>
      <Stack spacing={5} ml="xs" mb="md">
        <Checkbox
          disabled
          label="Name des Kunden"
          {...form.getInputProps('includeCustomerName', { type: 'checkbox' })}
        />
        <Checkbox
          disabled={isEmpty(phoneNumber)}
          label="Telefonnummer des Kunden"
          {...form.getInputProps('includeCustomerPhone', { type: 'checkbox' })}
        />
        <Checkbox
          disabled={isEmpty(email)}
          label="E-Mail des Kunden"
          {...form.getInputProps('includeCustomerEmail', { type: 'checkbox' })}
        />
        <Checkbox label="Kundenadresse" {...form.getInputProps('includeCustomerAddress', { type: 'checkbox' })} />
        <Checkbox label="Locationname" {...form.getInputProps('includeLocation', { type: 'checkbox' })} />
        <Checkbox
          disabled={!kind}
          label="Art des Event"
          {...form.getInputProps('includeEventKind', { type: 'checkbox' })}
        />
      </Stack>
      <Divider my="md" />
      <Select
        mb="xs"
        label={
          <Text size="xs" color="dimmed">
            Vorlage
          </Text>
        }
        icon={<IconTemplate size={20} />}
        data={DataS.getAgencyTemplateOptions(templates)}
        value={selectedTemplate ? selectedTemplate.id : undefined}
        nothingFound="Keine Vorlagen"
        onChange={(val) => selectTemplate(templates.find((t) => t.id === val))}
        styles={{ rightSection: { pointerEvents: 'none' } }}
        rightSection={<IconChevronDown size={14} />}
        rightSectionWidth={25}
      />

      <EventBaseInput
        updateTrigger={lastUpdate}
        toolbar={<BaseToolbar />}
        label="Betreff"
        value={subject}
        onUpdate={(html) => updateProp({ subject: html })}
      />
      <Space h="sm" />
      <Attachments
        attachments={attachments}
        documentBlueprints={[]}
        deleteFileAttachment={deleteFileAttachment}
        deleteDocumentAttachment={undefined}
        eventId={undefined}
      />
      <Space h={attachments.length > 0 ? 'xl' : 'sm'} />
      <Text mb="sm">{`Hey ${RecommendationS.getGreeting(recommendedContact)},`} </Text>
      <Text mb="sm"> unten findest Du die Kunden- und Event-Daten zum Auftrag.</Text>
      <EventRichInput
        updateTrigger={lastUpdate}
        isEmailInput
        styles={TiptapS.TextAreaStyles}
        label="Nachricht an den Dienstleister"
        value={message}
        onUpdate={(html) => updateProp({ message: html })}
        toolbar={
          <TemplateMessageToolbar
            addAttachment={() =>
              eventId &&
              profileId &&
              openAttachmentSelectionModal({
                eventId: eventId.toString(),
                profileId,
                initialAttachmnets: attachments,
                onSave: (_attachments) => {
                  setAttachments(_attachments);
                  return Promise.resolve(true);
                },
              })
            }
            saveAsTemplate={() => openCreateTemplateModal({ templateDto: { subject, message, attachments } })}
          />
        }
      />
      <Center mt="md">
        <Button
          mt="xl"
          mb="sm"
          disabled={loading}
          onClick={onSubmit}
          leftIcon={loading ? <Loader size="xs" /> : <IconSend size={16} />}
          sx={{ minWidth: 250 }}
        >
          {i18n._('actions.send')}
        </Button>
      </Center>
    </>
  );
};

export default SendCustomerInfoMail;
